<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import { usePdfViewerStore } from "../stores/pdfViewer.ts";
import { getPdfUrl } from "../utils/PdfUtils.ts";
// The legacy build, because the modern one calls brand-new built-ins (Math.sumPrecise,
// Map.prototype.getOrInsertComputed, Uint8Array.fromBase64) unpolyfilled, which older
// Safari and Chrome versions lack. The legacy build carries polyfills for them.
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import workerUrl from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";

// The worker is a module script served from 'self' — works under strict CSP.
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

const { t } = useI18n();
const router = useRouter();
const store = usePdfViewerStore();

const canvases = ref<HTMLCanvasElement[]>([]);
const totalPages = ref(0);
const loading = ref(true);
const error = ref(false);

const filename = computed(() => store.filename);

async function renderPdf(blob: Blob) {
    const data = await blob.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    totalPages.value = pdf.numPages;
    loading.value = false;
    await nextTick();
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvases.value[i - 1];
        if (!canvas) continue;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvas, viewport }).promise;
    }
}

function downloadPdf() {
    if (!store.blob) return;
    const url = URL.createObjectURL(store.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = store.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function goBack() {
    store.clear();
    router.push(store.returnRoute);
}

function onKeyDown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        downloadPdf();
    }
}

onMounted(async () => {
    globalThis.addEventListener("keydown", onKeyDown);

    let blob = store.blob;

    if (!blob) {
        if (!store.fetchParams) {
            router.replace(store.returnRoute);
            return;
        }
        try {
            const result = await getPdfUrl(
                store.fetchParams.trainer,
                store.fetchParams.product,
                store.fetchParams.participantId,
                store.fetchParams.language,
            );
            const normalizedFilename = result.filename.toLowerCase().endsWith(".pdf")
                ? result.filename
                : `${result.filename}.pdf`;
            store.blob = result.blob;
            store.filename = normalizedFilename;
            blob = result.blob;
        } catch (e) {
            console.error("PDF refetch error:", e);
            error.value = true;
            loading.value = false;
            return;
        }
    }

    try {
        await renderPdf(blob);
    } catch (e) {
        console.error("PDF render error:", e);
        error.value = true;
        loading.value = false;
    }
});

onUnmounted(() => {
    globalThis.removeEventListener("keydown", onKeyDown);
});
</script>

<template>
    <div class="pdf-viewer">
        <div class="pdf-toolbar">
            <Button
                icon="pi pi-arrow-left"
                :label="t('buttons.back')"
                text
                class="pdf-toolbar__back"
                @click="goBack"
            />
            <span class="pdf-toolbar__filename">{{ filename }}</span>
            <Button
                icon="pi pi-download"
                :label="t('buttons.download')"
                class="pdf-toolbar__download"
                @click="downloadPdf"
            />
        </div>

        <div v-if="loading" class="pdf-viewer__loading">
            <i class="pi pi-spin pi-spinner" style="font-size: 2rem" />
        </div>

        <div v-else-if="error" class="pdf-viewer__error" :data-error-message="t('errors.unexpectedError')">
            <p>{{ t('errors.unexpectedError') }}</p>
            <Button :label="t('buttons.back')" @click="goBack" />
        </div>

        <div v-else class="pdf-viewer__pages" :data-total-pages="totalPages">
            <canvas
                v-for="n in totalPages"
                :key="n"
                :ref="(el) => { if (el) canvases[n - 1] = el as HTMLCanvasElement }"
                class="pdf-viewer__canvas"
            />
        </div>
    </div>
</template>

<style scoped>
.pdf-viewer {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: #525659;
}

.pdf-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    background: #323639;
    color: #fff;
    flex-shrink: 0;
}

.pdf-toolbar__back {
    color: #fff !important;
}

.pdf-toolbar__filename {
    flex: 1;
    text-align: center;
    font-size: 14px;
    color: #ccc;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pdf-toolbar__download {
    flex-shrink: 0;
}

.pdf-viewer__loading,
.pdf-viewer__error {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    gap: 16px;
}

.pdf-viewer__pages {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 0;
    gap: 16px;
}

.pdf-viewer__canvas {
    max-width: 100%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
</style>
